import { TableLayoutTemplateProvider } from '@aibees/shared/features/advance-table/components/table-layout-modifier-dialog/context';
import { classnames } from '@aibees/shared/utils';
import { isEqual } from 'lodash-es';
import React, { memo, useMemo, useRef } from 'react';
import { Table } from './components/table';
import { TableFilters } from './components/table-filters';
import { AdvanceTableContextProvider } from './context';
import * as Styled from './styles';
import type { AdvanceTableConfig, AdvanceTableProps as Props } from './types';
import { addExtraFilter } from './utils';

function Wrapper(props: {
  hasColumnModifier?: boolean;
  children?: React.ReactNode;
}) {
  if (props.hasColumnModifier)
    return (
      <TableLayoutTemplateProvider>
        {props.children}
      </TableLayoutTemplateProvider>
    );
  return props.children;
}

export const AdvanceTable = memo(
  ({ className, config, extraFilters, afterEl, beforeEl }: Props) => {
    const prevConfigRef = useRef<AdvanceTableConfig>();
    const memoizedConfig = useRef<AdvanceTableConfig>();

    const configWithExtraFilters = useMemo(() => {
      const newConfig = addExtraFilter(config, extraFilters);
      if (!isEqual(prevConfigRef.current, newConfig)) {
        prevConfigRef.current = newConfig;
        memoizedConfig.current = newConfig;
      }
      return memoizedConfig.current!;
    }, [config, extraFilters]);

    return (
      <AdvanceTableContextProvider config={configWithExtraFilters}>
        <Wrapper hasColumnModifier={!!config.columnModifierPresetType}>
          {beforeEl}
          <Styled.Wrapper
            className={classnames('AdvanceTable', className, {
              'rounded-header': config.styles?.roundedHeader !== false,
            })}
            sx={config.sx}
          >
            {!!configWithExtraFilters.filters?.length && <TableFilters />}
            <Table />
          </Styled.Wrapper>
          {afterEl}
        </Wrapper>
      </AdvanceTableContextProvider>
    );
  }
);

AdvanceTable.displayName = 'AdvanceTable';
